import { Currency } from 'domain/Currency/Currency'
import { GooglePlace, ILocationModelJSON, isAddress, Port, PortOrAddress } from 'features/Directory'
import { ShipmentMode, ShipmentType } from 'features/ShipmentDetails'
import { ISO } from 'service/ISO'

import { Cargo, LCLCargoJSON } from './Cargo'
import { CustomsType } from './CustomsType'
import { BookingParty } from './Incoterms/incotermsMeta'
import { IInsurance } from './InsuranceModel'
import { BuyersConsolidationQuoteServicesJSON } from './QuoteRequest/BuyersConsolidationQuoteServicesJSON'
import { InsuranceQR, serializeInsuranceQR, toInsuranceQR } from './QuoteRequestInsurance'
import { CarbonOffsetStrategy } from './QuoteServices'
import { IQuoteSheet } from './QuoteSheet/QuoteSheetModel'

export function fromQuoteSheet(sheet: IQuoteSheet): IQuoteRequest {
  const { request } = sheet
  const quoteRequest: IQuoteRequest = {
    type: request.type,
    modes: request.modes,
    origin: request.journey.origin,
    destination: request.journey.destination,
    cargo: request.cargo,
    cargoStackable: request.services?.isStackable as boolean,
    cargoReadyAt: request.cargoReadyAt ? new Date(request.cargoReadyAt) : null,
    customs:
      request.services?.customsSelected || (!!request.services?.customsIssuance && !!request.services.customsBondType),
    hsCodesCount: request.services?.hsCodesCount as number,
    insuranceEnabled: !!(request.services?.insurances && (request.services?.insurances.length as number) > 0),
    insurance: toInsuranceQR(request.services?.insurances ?? []),
    specialEquipment: request.services?.specialEquipment as boolean,
    dangerousGoods: request.services?.dangerousGoods as boolean,
    dangerousGoodsAdditionalDetails: request.services?.dangerousGoodsAdditionalDetails,
    goodsValue: request.goodsValue,
    customsIssuance: request.services?.customsIssuance as boolean,
    customsBondType: request.services?.customsBondType || '',
    vgm: request.services?.vgm,
    // "as unknown as BuyersConsolidationQuoteServicesJSON" to keep betterer happy without the need of refactor
    // This will be fixed with the non mobx version
    buyersConsolidation: null as unknown as BuyersConsolidationQuoteServicesJSON,
    incoTerm: request.incoTerm,
    bookingParty: request.bookingParty,
    isBookingPartyAgent: request.isBookingPartyAgent,
    carbonOffsetService: request.services?.carbonOffset,
  }
  return quoteRequest
}

export interface QuoteRequestJSON {
  _id?: string
  cargo: Cargo[]
  cargoReadyAt?: Date
  customs: boolean
  customsIssuance: boolean
  dangerousGoods?: boolean
  dangerousGoodsAdditionalDetails?: string
  destination?: string
  hsCodesCount: number
  insurance: IInsurance[]
  modes: ShipmentMode[]
  origin?: string
  pod?: string
  pol?: string
  requestedCustomsTypes: CustomsType[]
  specialEquipment: boolean
  type: ShipmentType
  vgm?: boolean
  buyersConsolidation?: BuyersConsolidationQuoteServicesJSON
  customsBondType?: string
  incoTerm?: ISO.IncoTermsCode
  bookingParty?: BookingParty
  carbonOffsetService?: {
    offsetStrategy: CarbonOffsetStrategy
  }
  isBookingPartyAgent?: boolean
}

interface LCLQuoteRequestSerialized extends QuoteRequestJSON {
  type: 'lcl'
  modes: ShipmentMode[]
  cargo: LCLCargoJSON[]
  cargoReadyAt: Date
  isStackable: boolean
  goodsValue: Currency
}

export const getUrlQuoteRequest = (windowLocationSearch: string): QuoteRequestJSON => {
  const urlParams = new URLSearchParams(windowLocationSearch)
  return JSON.parse(urlParams.get('request') as string)
}

export interface IQuoteRequest {
  cargo: Cargo[]
  cargoReadyAt: Date | null
  cargoStackable: boolean
  customs: boolean
  customsBondType: string
  customsIssuance: boolean
  dangerousGoods: boolean
  dangerousGoodsAdditionalDetails?: string
  destination: PortOrAddress

  goodsValue?: Currency
  hsCodesCount: number
  insurance: InsuranceQR[]
  insuranceEnabled: boolean
  modes: ShipmentMode[]
  origin: PortOrAddress
  specialEquipment: boolean
  type: ShipmentType
  vgm?: boolean
  buyersConsolidation?: BuyersConsolidationQuoteServicesJSON
  incoTerm?: ISO.IncoTermsCode
  bookingParty?: BookingParty
  carbonOffsetService?: {
    offsetStrategy: CarbonOffsetStrategy
  }
  isBookingPartyAgent?: boolean
}

export type TPlaceData = Port | GooglePlace | ILocationModelJSON

const customsTypesToJSON = (customs: boolean): CustomsType[] => {
  const customsTypes: CustomsType[] = []
  if (customs) {
    customsTypes.push('standard', 'fiscal', 't1', 't1_only')
  }
  return customsTypes
}

const extractInsurances = (qr: IQuoteRequest): IInsurance[] => {
  return qr.insuranceEnabled ? serializeInsuranceQR(qr.insurance) : []
}

interface QROrigin {
  pol?: string
  origin?: string
  originLocationId?: string
}

interface QRDestination {
  pod?: string
  destination?: string
  destinationLocationId?: string
}

abstract class Builder<T extends QRDestination | QROrigin> {
  transformPlace(place: PortOrAddress): this {
    // Template method to transform place
    if (isAddress(place)) {
      if (place.locationId) {
        this.addLocation(place.locationId)
      } else {
        this.addPlace((place.place_id || place.googplace_id || place.googlePlace) as string)
      }
    } else {
      this.addPort(place._id)
    }
    return this
  }
  protected abstract addLocation(locationId: string): void
  protected abstract addPlace(place: string): void
  protected abstract addPort(port: string): void
  abstract build(): T
}

class QROriginBuilder extends Builder<QROrigin> {
  private origin: QROrigin = {}

  addLocation(locationId: string): void {
    this.origin.originLocationId = locationId
  }

  addPlace(place: string): void {
    this.origin.origin = place
  }

  addPort(port: string): void {
    this.origin.pol = port
  }

  build(): QROrigin {
    return this.origin
  }
}

class QRDestinationBuilder extends Builder<QRDestination> {
  private destination: QRDestination = {}

  addLocation(locationId: string): void {
    this.destination.destinationLocationId = locationId
  }

  addPlace(place: string): void {
    this.destination.destination = place
  }

  addPort(port: string): void {
    this.destination.pod = port
  }

  build(): QRDestination {
    return this.destination
  }
}

function serializeDestination(place: PortOrAddress): QRDestination {
  return new QRDestinationBuilder().transformPlace(place).build()
}

function serializeOrigin(place: PortOrAddress): QROrigin {
  return new QROriginBuilder().transformPlace(place).build()
}

export function serializeRequest(qr: IQuoteRequest): QuoteRequestJSON {
  let qrs: QuoteRequestJSON = {
    type: qr.type,
    modes: qr.modes && qr.modes.length > 0 ? qr.modes : ['sea'],
    customs: qr.customs,
    requestedCustomsTypes: customsTypesToJSON(qr.customs),
    insurance: extractInsurances(qr),
    cargo: qr.cargo.filter((c) => c.type === qr.type),
    hsCodesCount: qr.hsCodesCount ? Number(qr.hsCodesCount) : 1,
    specialEquipment: !!qr.specialEquipment,
    dangerousGoods: !!qr.dangerousGoods,
    dangerousGoodsAdditionalDetails: qr.dangerousGoodsAdditionalDetails,
    ...serializeDestination(qr.destination),
    ...serializeOrigin(qr.origin),
    customsIssuance: qr.customsIssuance,
    buyersConsolidation: qr.buyersConsolidation,
    cargoReadyAt: qr.cargoReadyAt as Date,
    customsBondType: qr.customsBondType,
    incoTerm: qr.incoTerm,
    bookingParty: qr.bookingParty,
    carbonOffsetService: qr.carbonOffsetService,
    isBookingPartyAgent: qr.isBookingPartyAgent,
  }
  if (qr.type === 'fcl') {
    qrs = {
      ...qrs,
      vgm: qr.vgm,
    }
  } else if (qr.type === 'lcl') {
    qrs = {
      ...qrs,
      isStackable: qr.cargoStackable,
      goodsValue: qr.goodsValue,
    } as LCLQuoteRequestSerialized
  }
  return qrs
}
